import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { getAuditLogs, type AuditLog } from '@/services'
import { AppButton, EmptyState, Drawer, TableSkeleton, rowActivation } from '@/components/platform/primitives'
import { Icon } from '@/components/platform/Icon'
import { relTime, formatDateTime } from '@/lib/utils'

export function AuditPage() {
  const [search, setSearch] = useState('')
  const [selected, setSelected] = useState<AuditLog | null>(null)

  const { data: result, isLoading } = useQuery({
    queryKey: ['audit-logs'],
    queryFn: () => getAuditLogs({ pageSize: 100 }),
  })

  const logs = result?.data || []
  const total = result?.count || logs.length
  const filtered = search
    ? logs.filter((l) =>
        [l.action, l.entity_type, l.entity_id, l.tenant_id].some((v) => (v || '').toLowerCase().includes(search.toLowerCase())))
    : logs

  return (
    <>
      <div className="page-header">
        <div className="page-title-block">
          <div className="eyebrow">Soporte</div>
          <h1>Audit log</h1>
          <div className="subtitle">{total} acciones registradas</div>
        </div>
        <div className="page-actions">
          <AppButton variant="secondary" icon="download">Exportar</AppButton>
        </div>
      </div>

      <div className="table-wrap">
        <div className="table-toolbar">
          <div className="table-search">
            <Icon name="search" size={14} />
            <input placeholder="Buscar acción, entidad o tenant…" value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
        </div>

        {isLoading ? (
          <TableSkeleton columns={5} rows={9} />
        ) : filtered.length === 0 ? (
          <EmptyState icon="shield" title="Sin eventos" desc="No hay acciones registradas con ese filtro." />
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Cuándo</th>
                <th>Usuario</th>
                <th>Acción</th>
                <th>Entidad</th>
                <th>Tenant</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((log) => (
                <tr key={log.id} onClick={() => setSelected(log)} {...rowActivation(() => setSelected(log))}>
                  <td><span style={{ fontSize: 12, color: 'var(--text-3)' }}>{relTime(log.created_at)}</span></td>
                  <td><span style={{ fontSize: 12.5 }}>{log.user_id ? log.user_id.slice(0, 8) : 'sistema'}</span></td>
                  <td><span className="badge badge-outline badge-sm">{log.action}</span></td>
                  <td>
                    <div style={{ fontSize: 13 }}>{log.entity_type || '—'}</div>
                    {log.entity_id ? <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text-3)', marginTop: 2 }}>{log.entity_id.slice(0, 12)}</div> : null}
                  </td>
                  <td><span style={{ fontSize: 12.5 }}>{log.tenant_id ? log.tenant_id.slice(0, 8) : '—'}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Drawer open={!!selected} onClose={() => setSelected(null)} title={selected ? `Audit event ${selected.id.slice(0, 8)}` : ''}>
        {selected ? (
          <div className="col gap-4">
            <div className="card">
              <div className="card-body">
                <dl className="dl" style={{ gridTemplateColumns: '110px 1fr' }}>
                  <dt>Acción</dt><dd><span className="badge badge-outline badge-sm">{selected.action}</span></dd>
                  <dt>Entidad</dt><dd>{selected.entity_type || '—'}{selected.entity_id ? ` · ${selected.entity_id}` : ''}</dd>
                  <dt>Usuario</dt><dd>{selected.user_id || 'sistema'}</dd>
                  <dt>Tenant</dt><dd>{selected.tenant_id || '—'}</dd>
                  <dt>Cuándo</dt><dd>{formatDateTime(selected.created_at)}</dd>
                  <dt>IP</dt><dd>{selected.ip_address || '—'}</dd>
                </dl>
              </div>
            </div>
            {selected.old_data || selected.new_data ? (
              <div className="card">
                <div className="card-header"><h3>Cambios</h3></div>
                <div className="card-body">
                  <pre style={{ margin: 0, padding: 14, background: 'var(--bisne-dark)', color: 'var(--text-inverse)', fontSize: 11.5, fontFamily: 'var(--font-mono)', borderRadius: 8, lineHeight: 1.55, overflow: 'auto' }}>{JSON.stringify({ old: selected.old_data, new: selected.new_data }, null, 2)}</pre>
                </div>
              </div>
            ) : null}
          </div>
        ) : null}
      </Drawer>
    </>
  )
}
